import { useState } from 'react';

import { useIntl } from 'react-intl-next';
import { usePathParam } from 'react-resource-router';

import { useDefaultCatch } from '@atlassian/eoc-catch';
import { errorToastProps, successToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';

import {
	type CreateEscalationParams,
	type EscalationsCommonService,
	type UpdateEscalationParams,
	type UpdatePartialEscalationParams,
} from '../../common/types';

import {
	createEscalationEndpoint,
	deleteEscalationEndpoint,
	updateEscalationEndpoint,
	updatePartialEscalationEndpoint,
} from './endpoints';
import messages from './messages';

export const useEscalationsCommonService = (): EscalationsCommonService => {
	const defaultCatch = useDefaultCatch();
	const [teamId] = usePathParam('teamId');
	const [isCreating, setIsCreating] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const [isPartiallyUpdating, setIsPartiallyUpdating] = useState(false);
	const flagApi = useFlags();
	const { formatMessage } = useIntl();

	const createEscalation = (escalationPolicy: CreateEscalationParams) => {
		setIsCreating(true);

		return createEscalationEndpoint(teamId, escalationPolicy)
			.then((res) => {
				flagApi.showFlag({
					...successToastProps(),
					title: formatMessage(messages.escalationPolicyAdded, {
						name: escalationPolicy.name,
					}),
				});

				return res;
			})
			.catch((error) => {
				defaultCatch(
					{
						id: 'useEscalationsCommonService-createEscalation',
						httpErrorHandler: (err) => {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: formatMessage(messages.createError),
							});
						},
					},
					error,
				);

				throw error;
			})
			.finally(() => setIsCreating(false));
	};

	const updateEscalation = (escalationPolicy: UpdateEscalationParams) => {
		setIsUpdating(true);

		return updateEscalationEndpoint(teamId, escalationPolicy)
			.then((res) => {
				flagApi.showFlag({
					...successToastProps(),
					title: formatMessage(messages.escalationPolicySaved, {
						name: escalationPolicy.name,
					}),
				});

				return res;
			})
			.catch((error) => {
				defaultCatch({
					id: 'useEscalationsCommonService-updateEscalation',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.updateError),
						});
					},
				});

				throw error;
			})
			.finally(() => setIsUpdating(false));
	};

	const updatePartialEscalation = (escalationPolicy: UpdatePartialEscalationParams) => {
		setIsPartiallyUpdating(true);

		return updatePartialEscalationEndpoint(teamId, escalationPolicy)
			.catch((error) => {
				defaultCatch(
					{
						id: 'useEscalationsCommonService-updatePartialEscalation',
						httpErrorHandler: (err) => {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: formatMessage(messages.changeStateError, {
									isEnabled: escalationPolicy.enabled,
								}),
							});
						},
					},
					error,
				);

				throw error;
			})
			.finally(() => setIsPartiallyUpdating(false));
	};

	const deleteEscalation = (id: string, name: string) => {
		setIsDeleting(true);

		return deleteEscalationEndpoint(teamId, id)
			.then(() => {
				flagApi.showFlag({
					...successToastProps(),
					title: formatMessage(messages.escalationPolicyDeleted, { name }),
				});
			})
			.catch((error) => {
				defaultCatch(
					{
						id: 'useEscalationsCommonService-deleteEscalation',
						httpErrorHandler: (err) => {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: formatMessage(messages.deleteError),
							});
						},
					},
					error,
				);

				throw error;
			})
			.finally(() => setIsDeleting(false));
	};

	return {
		isCreating,
		isDeleting,
		isUpdating,
		isPartiallyUpdating,

		createEscalation,
		deleteEscalation,
		updateEscalation,
		updatePartialEscalation,
	};
};
